/**
 * Which expiry a strategy means — ROADMAP 5.17.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * RESOLVED FROM LISTED CONTRACTS, NEVER FROM A CALENDAR
 *
 * A strategy says "weekly", not "14 October". The mapping is only knowable
 * from what the exchange has actually listed, and computing it from the
 * calendar is a bug that hides for months and then trades the wrong week.
 *
 * The reference backtest engine falls back to calendar arithmetic when its
 * database returns nothing — Tuesday for weeklies, last Thursday for
 * monthlies. Those two rules already contradict each other, and NSE has moved
 * expiry days more than once. There is no fallback here: no listed contract
 * means the answer is refused, not guessed.
 *
 * MONTHLY IS DERIVED, NOT FLAGGED
 *
 * No feed marks an expiry "monthly". It is simply the LAST expiry of its
 * calendar month, which is a property of the list rather than of any one
 * date — so the whole list is grouped by month and the last of each is taken.
 * That also handles the roll correctly: once October's monthly has passed,
 * the first remaining month-end is November's, and nothing has to special-case
 * the last week of a month.
 * ─────────────────────────────────────────────────────────────────────────────
 */
import { istDateKey, istParts } from './istClock.js';
import { ORDER_WINDOW_IST } from './marketWindow.js';
/**
 * Has this contract's last trading day passed?
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * TWO KINDS OF DATE, COMPARED AS STRINGS ON PURPOSE
 *
 * An expiry is a CALENDAR DATE — `@db.Date`, no time of day — so it is read in
 * UTC, which is how Prisma hands it back and how `expiryLabelOf` already reads
 * it. "Today" is an IST DAY, because that is the day the exchange is open: a
 * UTC boundary would roll at 05:30 IST and call this morning yesterday.
 * `YYYY-MM-DD` keys order lexically, so the comparison needs no arithmetic.
 *
 * STRICTLY BEFORE TODAY, NEVER "ON OR BEFORE". A contract expiring TODAY is
 * live until 15:30 IST and is an ordinary position all morning. Flagging it at
 * IST midnight would pull a tradeable leg out of the book on the one day a
 * trader most needs to act on it. Same boundary `resolveExpiry` uses above to
 * decide which expiries are still worth offering.
 *
 * AN UNREADABLE EXPIRY IS NOT AN EXPIRED ONE. A date that will not parse says
 * nothing about the contract, and the three-valued rule applies here as it does
 * to a position read — held / expired / unknown — with unknown never reported
 * as the state that takes a position out of every total. An empty or malformed
 * key sorts before every real date, so it has to be rejected rather than
 * compared.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function contractExpired(expiry, now = new Date()) {
    const key = typeof expiry === 'string' ? expiry.slice(0, 10) : isoDateOf(expiry);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(key))
        return false;
    return key < istDateKey(now);
}
const isoDateOf = (d) => Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
export const EXPIRY_KINDS = ['WEEKLY', 'NEXT_WEEKLY', 'MONTHLY', 'NEXT_MONTHLY'];
/** YYYY-MM from a YYYY-MM-DD key. */
const monthOf = (isoDate) => isoDate.slice(0, 7);
/**
 * The expiries that are the last of their calendar month, in order.
 *
 * Exported because the strategy edit form (5.19) shows a trader which real
 * date each kind currently means, and recomputing that rule in the UI is how
 * the two drift.
 */
export function monthlyExpiries(sorted) {
    const lastOfMonth = new Map();
    // The input is ascending, so each write for a month supersedes the previous
    // and the final one is that month's last.
    for (const d of sorted)
        lastOfMonth.set(monthOf(d), d);
    return [...lastOfMonth.values()];
}
/**
 * The earliest expiry still worth offering, as an IST day key — ROADMAP 9.44.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * TODAY'S EXPIRY STOPS BEING AN EXPIRY WHEN THE ORDER WINDOW SHUTS
 *
 * The rule used to be "on or after today", all day. That is right until the
 * close and wrong after it: at 18:43 the chain still opened on a contract
 * that settled hours ago, `resolveExpiry('WEEKLY')` still answered with it,
 * and a trader who opened the software in the evening to plan tomorrow was
 * looking at a dead ladder with no time value in it.
 *
 * The boundary is the ORDER window's close rather than the trading window's,
 * because that is this system's own definition of when a day is over — the
 * same 16:00 that decides whether an order may be sent at all. One cutoff,
 * one constant; a second number here would drift from it.
 *
 * STRICTLY BEFORE THE CLOSE, so a contract expiring TODAY is an ordinary
 * tradeable position for the whole session. Pulling it at IST midnight — or
 * at 15:30 — would take a live leg off the ladder on the one day a trader
 * most needs to act on it.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function earliestOfferedExpiry(now = new Date()) {
    const today = istDateKey(now);
    const p = istParts(now);
    if (p.h * 60 + p.min < ORDER_WINDOW_IST.closeMin)
        return today;
    // Past the close: today is done, so tomorrow is the earliest. Computed by
    // stepping the IST day rather than by adding 24h to `now`, which would be
    // wrong across a DST boundary in any zone that has one.
    const next = new Date(`${today}T00:00:00.000Z`);
    next.setUTCDate(next.getUTCDate() + 1);
    return next.toISOString().slice(0, 10);
}
/** Listed expiries still worth offering, deduped and ascending. */
export function offerableExpiries(listed, now = new Date()) {
    const from = earliestOfferedExpiry(now);
    return [...new Set(listed)].filter((d) => d >= from).sort();
}
/**
 * Turn an expiry kind into a real listed date.
 *
 * `listed` is every expiry the catalogue holds for one underlying, in any
 * order; it is sorted and filtered here so a caller cannot get that wrong.
 * Dates before today are dropped — an expiry that has passed is not a
 * contract anyone can trade into.
 */
export function resolveExpiry(kind, listed, now = new Date()) {
    const upcoming = offerableExpiries(listed, now);
    if (upcoming.length === 0) {
        return { ok: false, why: 'No expiry is listed on or after today for this underlying.' };
    }
    switch (kind) {
        case 'WEEKLY':
            return { ok: true, date: upcoming[0] };
        case 'NEXT_WEEKLY': {
            const d = upcoming[1];
            /*
             * Refused rather than falling back to the near weekly. A trader who
             * chose the NEXT week does not want this week's expiry — on expiry day
             * that is a position with hours of life rather than a week, which is a
             * different trade with different risk.
             */
            return d
                ? { ok: true, date: d }
                : { ok: false, why: 'Only one expiry is listed; there is no next weekly yet.' };
        }
        case 'MONTHLY': {
            const d = monthlyExpiries(upcoming)[0];
            return d ? { ok: true, date: d } : { ok: false, why: 'No monthly expiry is listed.' };
        }
        case 'NEXT_MONTHLY': {
            const d = monthlyExpiries(upcoming)[1];
            return d
                ? { ok: true, date: d }
                : { ok: false, why: 'Only one month of expiries is listed; there is no next monthly yet.' };
        }
    }
}
/**
 * Which kinds a real listed date currently answers to — ROADMAP 9.42.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE INVERSE OF `resolveExpiry`, AND A SET RATHER THAN ONE ANSWER
 *
 * `resolveExpiry` goes kind -> date, which is what a strategy needs before it
 * is placed. Once a chain is ON SCREEN the question runs the other way: the
 * trader has picked 2026-11-09, a leg says WEEKLY, and somebody has to decide
 * whether that leg is asking for the chain already in hand or for a second one.
 *
 * IT RETURNS SEVERAL BECAUSE A DATE GENUINELY IS SEVERAL. In the week a
 * monthly settles, the nearest weekly and the nearest monthly are the same
 * contract — `resolveExpiry('WEEKLY')` and `resolveExpiry('MONTHLY')` return
 * the identical date — so a chain that satisfies only one of the two would
 * refuse a monthly strategy on the one chain that could trade it.
 *
 * IT IS COMPUTED BY RUNNING `resolveExpiry` RATHER THAN BY REIMPLEMENTING IT.
 * Four calls over a short list is nothing, and it makes drift impossible: the
 * "last of its calendar month" rule, the "strictly before today" boundary and
 * the IST day all have exactly one definition, above.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function kindsOfExpiry(date, listed, now = new Date()) {
    const key = date.slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(key))
        return [];
    return EXPIRY_KINDS.filter((kind) => {
        const pick = resolveExpiry(kind, listed, now);
        return pick.ok && pick.date === key;
    });
}
//# sourceMappingURL=expiryKind.js.map